import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api } from '../lib/api';

interface User {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role: string;
  tier: {
    id: string;
    name: string;
    slug: string;
  };
  subscription_status: string | null;
  email_verified: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  register: (data: { email: string; password: string; first_name?: string; last_name?: string }) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in on mount
    const token = api.getToken();
    if (token) {
      refreshUser().finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const refreshUser = async () => {
    const result = await api.getMe();
    if (result.success && result.data) {
      setUser(result.data);
    } else {
      setUser(null);
      api.setToken(null);
    }
  };

  const login = async (email: string, password: string) => {
    const result = await api.login(email, password);
    // login returns token at the top level, not under data
    if (result.success && result.token) {
      api.setToken(result.token);
      await refreshUser();
      return { success: true };
    }
    return { success: false, error: result.error?.message || 'Login failed' };
  };

  const register = async (data: { email: string; password: string; first_name?: string; last_name?: string }) => {
    const result = await api.register(data);
    if (result.success && result.token) {
      api.setToken(result.token);
      await refreshUser();
      return { success: true };
    }
    return { success: false, error: result.error?.message || 'Registration failed' };
  };

  const logout = async () => {
    await api.logout();
    api.setToken(null);
    setUser(null);
    window.location.hash = 'login';
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
